
import { useState, useEffect } from 'react';
import { getappsByUser, deleteapps } from '../components/DbServices';
import Layout from '../layout/Layout';
import AddAppModal from './AddAppModal';
import IntegrationGuideModal from './IntegrationGuideModal';
import PageTransition from '../components/PageTransition';
import { getStoredUser } from '../utilities/encryption';
import Swal from 'sweetalert2';

export default function AppList() {
  const user = getStoredUser();
  const userId = user?.data?.id;
  
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const [copiedId, setCopiedId] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [guideApp, setGuideApp] = useState(null);

  const fetchApiKeys = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const res = await getappsByUser(userId);
      setApiKeys(res.data || []);
    } catch (err) {
      console.error("Failed to fetch API keys", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApiKeys();
  }, [userId]);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const filteredKeys = apiKeys.filter(k => 
    k.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    k.api_key?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const currentItems = filteredKeys.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(filteredKeys.length / itemsPerPage);

  const handleRevoke = async (id) => {
    if (!window.confirm("External apps using this key will stop working immediately. Revoke access?")) return;
    try {
      const response = await deleteapps(id);
      if (response.data.status === 200) fetchApiKeys();
    } catch (err) {
      Swal.fire("Failed to revoke API key.");
    }
  };

  return (
    <Layout>
      <PageTransition>
        <div className="container-fluid py-4">
          
          {/* VIBRANT HEADER */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
            <div>
              <h3 className="fw-bold mb-1"><span className="color-theme">Developer Portal</span> 🚀</h3>
              <p className="text-muted small fw-medium">Connect external services via secure API keys.</p>
            </div>
            <button 
              className="btn btn-primary rounded-pill px-4 fw-bold shadow border-0"
              style={{ background: 'linear-gradient(45deg, #4f46e5, #9333ea)' }}
              onClick={() => setShowModal(true)}
            >
              <i className="bi bi-plus-circle-fill me-2"></i> Create New App
            </button>
          </div>

          {/* TABLE CONTAINER */}
          <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0 custom-admin-table">
                <thead style={{ backgroundColor: '#f8fafc' }}>
                  <tr className="text-muted small text-uppercase fw-bold">
                    <th className="ps-4 py-4">Application</th>
                    <th className="py-4">API Authentication</th>
                    <th className="py-4 text-center">Status</th>
                    <th className="py-4">Creation Date</th>
                    <th className="pe-4 py-4 text-end">Management</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="5" className="text-center py-5"><div className="spinner-border text-primary border-2"></div></td></tr>
                  ) : currentItems.length === 0 ? (
                    <tr><td colSpan="5" className="text-center py-5 text-muted">No API keys generated yet.</td></tr>
                  ) : (
                    currentItems.map(k => (
                      <tr key={k.id} className="border-bottom border-light">
                        <td className="ps-4 py-3">
                          <div className="d-flex align-items-center gap-3">
                            <div className="rounded-3 d-flex align-items-center justify-content-center shadow-sm bg-dark text-white" 
                                 style={{ width: '38px', height: '38px' }}>
                              <i className="bi bi-layers-half"></i>
                            </div>
                            <span className="fw-bold text-dark">{k.name}</span>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2 bg-light-subtle border rounded-2 px-2 py-1" style={{ width: 'fit-content' }}>
                            <code className="text-indigo fw-bold x-small">{k.api_key}</code>
                            <button className="btn btn-link p-0 text-muted shadow-none border-0" onClick={() => copyToClipboard(k.api_key, k.id)}>
                              {copiedId === k.id ? <i className="bi bi-check2 text-success"></i> : <i className="bi bi-clipboard"></i>}
                            </button>
                          </div>
                        </td>
                        <td className="text-center">
                          <span className={`badge rounded-pill fw-bold ${k.status === '1' ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'} px-3 py-2`} style={{ fontSize: '10px' }}>
                            <span className={`pulse-dot me-2 ${k.status === '1' ? 'bg-success' : 'bg-danger'}`}></span>
                            {k.status === '1' ? 'ACTIVE' : 'REVOKED'}
                          </span>
                        </td>
                        <td>
                          <div className="text-muted small fw-medium">
                            <i className="bi bi-calendar3 me-2"></i>
                            {new Date(k.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                          </div>
                        </td>
                        <td className="pe-4 text-end">
                          <div className="d-flex justify-content-end gap-2">
                            <button 
                                className="btn btn-light-indigo btn-sm border-0 rounded-3 fw-bold px-3" 
                                onClick={() => setGuideApp(k)}
                            >
                              <i className="bi bi-code-slash me-1"></i> Documentation
                            </button>
                            <button 
                                className="btn btn-light-danger btn-sm border-0 rounded-3 px-2" 
                                title="Revoke Key"
                                onClick={() => handleRevoke(k.id)}
                            >
                              <i className="bi bi-shield-x"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* PAGINATION */}
          {!loading && totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-4 px-2">
              <p className="text-muted small mb-0">Total Applications: <strong>{apiKeys.length}</strong></p>
              <nav>
                <ul className="pagination pagination-sm mb-0 gap-1">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button className="page-link border-0 rounded-pill px-3 shadow-sm" onClick={() => setCurrentPage(prev => prev - 1)}>Previous</button>
                  </li>
                  {[...Array(totalPages)].map((_, i) => (
                    <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                      <button className={`page-link border-0 rounded-pill px-3 mx-1 ${currentPage === i + 1 ? 'shadow-sm' : ''}`} onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                    </li>
                  ))}
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button className="page-link border-0 rounded-pill px-3 shadow-sm" onClick={() => setCurrentPage(prev => prev + 1)}>Next</button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
      </PageTransition>

      {showModal && <AddAppModal userId={userId} onClose={() => { setShowModal(false); fetchApiKeys(); }} />}
      {guideApp && <IntegrationGuideModal app={guideApp} onClose={() => setGuideApp(null)} />}
    </Layout>
  );
}